// In-place text editor — HTML contentEditable overlaid on the canvas at the
// element's world position, converted to screen via viewport.
import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import type { TextElement } from '@extapia/shared';
import { worldToScreen } from '@extapia/canvas';
import { useCanvas } from '../state/canvas-store.js';

export const TextEditor = () => {
  const editingId = useCanvas((s) => s.editingTextId);
  const elements = useCanvas((s) => s.elements);
  const app = useCanvas((s) => s.app);
  const updateElement = useCanvas((s) => s.updateElement);
  const stopEditing = useCanvas((s) => s.stopTextEdit);
  const commit = useCanvas((s) => s.commit);
  const deleteEl = useCanvas((s) => s.deleteElement);
  const [text, setText] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const target =
    editingId != null
      ? (elements.find((e) => e.id === editingId && e.type === 'text' && !e.isDeleted) as
          | TextElement
          | undefined)
      : undefined;

  useEffect(() => {
    if (!target) return;
    setText(target.text);
    // focus next tick after render
    const t = setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
    return () => clearTimeout(t);
  }, [target?.id]);

  if (!target) return null;

  const screen = worldToScreen(
    { x: target.x, y: target.y },
    { scrollX: app.scrollX, scrollY: app.scrollY, zoom: app.zoom },
  );

  const commitText = () => {
    const trimmed = text;
    if (trimmed.length === 0) {
      deleteEl(target.id);
    } else {
      // Measure text size via a canvas 2D ctx
      const c = document.createElement('canvas');
      const ctx = c.getContext('2d')!;
      const fontMap: Record<number, string> = {
        1: '"Excalifont","Virgil",cursive',
        2: '"Nunito",sans-serif',
        3: '"Kalam",cursive',
        4: '"Cascadia Code",monospace',
      };
      ctx.font = `${target.fontSize}px ${fontMap[target.fontFamily]}`;
      const lines = trimmed.split('\n');
      const width = Math.max(1, ...lines.map((l) => ctx.measureText(l).width));
      const height = target.fontSize * target.lineHeight * lines.length;
      updateElement(target.id, { text: trimmed, originalText: trimmed, width, height });
    }
    commit();
    stopEditing();
  };

  const style: CSSProperties = {
    position: 'absolute',
    left: `${screen.x}px`,
    top: `${screen.y}px`,
    fontSize: `${target.fontSize * app.zoom}px`,
    lineHeight: target.lineHeight,
    color: target.strokeColor,
    background: 'transparent',
    border: '1px dashed rgba(14,165,233,0.5)',
    outline: 'none',
    padding: '0',
    margin: '0',
    resize: 'none',
    overflow: 'hidden',
    whiteSpace: 'pre',
    fontFamily:
      target.fontFamily === 1
        ? '"Excalifont","Virgil",cursive'
        : target.fontFamily === 2
        ? '"Nunito",sans-serif'
        : target.fontFamily === 3
        ? '"Kalam",cursive'
        : '"Cascadia Code",monospace',
    minWidth: '4ch',
    minHeight: `${target.fontSize * app.zoom}px`,
  };

  return (
    <textarea
      ref={inputRef}
      style={style}
      className="z-30"
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={commitText}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          setText(target.text);
          stopEditing();
        }
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
          e.preventDefault();
          commitText();
        }
      }}
    />
  );
};
